import { app } from "modular-openscriptjs";

const h = app("h");

/**
 * Aspect ratio presets offered by the full-screen image cropping tool.
 * `value: NaN` means "Free" - Cropper.js treats NaN as "no fixed ratio".
 */
export const CROP_ASPECT_RATIOS = [
    { key: "free", label: "Free", value: NaN },
    { key: "square", label: "Square", value: 1 },
    { key: "4-3", label: "4:3", value: 4 / 3 },
    { key: "3-4", label: "3:4", value: 3 / 4 },
    { key: "16-9", label: "16:9", value: 16 / 9 },
];

function aspectButtonClass(isActive) {
    return `btn btn-sm rounded-pill px-3 ${
        isActive ? "btn-primary" : "btn-light border"
    }`;
}

function utilityButton(ctx, { title, icon, method, args = [] }) {
    return h.button(
        {
            type: "button",
            class: "btn btn-sm btn-light border-0 d-flex align-items-center justify-content-center",
            style: "width: 36px; height: 36px;",
            title,
            onclick: ctx.method(method, args),
        },
        h.i({ class: `bi ${icon}`, style: "font-size: 1rem;" }),
    );
}

/**
 * Renders the toolbar for the full-screen cropper tool: aspect ratio
 * presets on one side, zoom / rotate / flip / reset utilities on the
 * other. `ctx` must be the component instance that owns `this.cropper.obj`
 * and implements setCropAspect/zoomCropper/rotateCropper/flipCropper/
 * resetCropper - the buttons call back into it via `ctx.method(...)`.
 */
export function renderCropperToolbar(ctx, activeAspectKey = "16-9") {
    return h.div(
        {
            id: "cropper-toolbar",
            class: "d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 border-bottom bg-white",
            style: "flex-shrink: 0;",
        },

        h.div(
            { class: "d-flex flex-wrap gap-2", id: "cropper-aspect-group" },
            ...CROP_ASPECT_RATIOS.map((ratio) =>
                h.button(
                    {
                        type: "button",
                        class: aspectButtonClass(ratio.key === activeAspectKey),
                        style: "font-size: 0.8rem;",
                        data_aspect_key: ratio.key,
                        onclick: ctx.method("setCropAspect", [ratio.key]),
                    },
                    ratio.label,
                ),
            ),
        ),

        h.div(
            { class: "d-flex align-items-center gap-1" },
            utilityButton(ctx, {
                title: "Zoom out",
                icon: "bi-zoom-out",
                method: "zoomCropper",
                args: [-0.1],
            }),
            utilityButton(ctx, {
                title: "Zoom in",
                icon: "bi-zoom-in",
                method: "zoomCropper",
                args: [0.1],
            }),
            h.div({ class: "vr mx-1", style: "height: 22px;" }),
            utilityButton(ctx, {
                title: "Rotate left",
                icon: "bi-arrow-counterclockwise",
                method: "rotateCropper",
                args: [-90],
            }),
            utilityButton(ctx, {
                title: "Rotate right",
                icon: "bi-arrow-clockwise",
                method: "rotateCropper",
                args: [90],
            }),
            h.div({ class: "vr mx-1", style: "height: 22px;" }),
            utilityButton(ctx, {
                title: "Flip horizontal",
                icon: "bi-symmetry-vertical",
                method: "flipCropper",
                args: ["horizontal"],
            }),
            utilityButton(ctx, {
                title: "Flip vertical",
                icon: "bi-symmetry-horizontal",
                method: "flipCropper",
                args: ["vertical"],
            }),
            h.div({ class: "vr mx-1", style: "height: 22px;" }),
            utilityButton(ctx, {
                title: "Reset",
                icon: "bi-arrow-repeat",
                method: "resetCropper",
                args: [],
            }),
        ),
    );
}

/**
 * Toggles the active/inactive styling on the aspect ratio pills after a
 * click, without needing a full re-render (this markup lives inside an
 * imperative tool.modal(), not a reactive component tree).
 */
export function setActiveAspectButton(dom, activeKey) {
    dom.all("[data-aspect-key]").forEach((btn) => {
        const isActive = btn.getAttribute("data-aspect-key") === activeKey;
        btn.className = aspectButtonClass(isActive);
    });
}

/**
 * tool.modal() always adds `modal-dialog-centered modal-dialog-scrollable`
 * to the dialog, and Bootstrap's "scrollable" variant lets `.modal-body`
 * grow to fit its content and scroll internally instead of capping its
 * height - which is exactly wrong for a cropper: the image (and the
 * scrollbar needed to see all of it) ends up taller than the viewport.
 * Inline styles here win over those (non-!important) component rules, so
 * this pins the dialog/content/body to the viewport height and lets our
 * own flex layout (toolbar + flex-grow-1 image area) divide up the space,
 * which is what lets Cropper.js scale the image down to fit instead of
 * rendering it at natural size.
 */
export function forceFullscreenModalLayout(modalRootElement) {
    const dialogEl = modalRootElement.querySelector(".modal-dialog");
    const contentEl = modalRootElement.querySelector(".modal-content");
    const bodyEl = modalRootElement.querySelector(".modal-body");

    if (dialogEl) {
        dialogEl.style.height = "100vh";
        dialogEl.style.width = "100vw";
        dialogEl.style.maxWidth = "none";
        dialogEl.style.margin = "0";
    }

    if (contentEl) {
        contentEl.style.height = "100vh";
        contentEl.style.maxHeight = "100vh";
        contentEl.style.overflow = "hidden";
    }

    if (bodyEl) {
        bodyEl.style.overflow = "hidden";
        bodyEl.style.minHeight = "0";
        bodyEl.style.display = "flex";
        bodyEl.style.flexDirection = "column";
    }
}
